// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useRef } from 'react';
import type { Client, ClientCreateDTO, ClientUpdateDTO, Gender, MaritalStatus } from '../types/client';
import type { RelationshipType } from '../types/relationship';

export type RelationshipUpdatePayload = { relationshipName: string; crmRelationshipNumber?: string };
import { Alert, AppIcon, InputLabel, DateTimeInput, Dropdown, Button, IconButton } from '@loa/ui-components';
import { FormInput } from './FormInput';
import { DEFAULT_COUNTRY, SUPPORTED_COUNTRIES, getCountryConfig, normalizePostalCode } from '../config/addressConfig';
import { PhoneFlagDropdown, DEFAULT_PHONE_COUNTRY, phoneCountries } from './PhoneFlagDropdown';
import { getCountryCallingCode } from 'react-phone-number-input';
import { clientApi, type EmailAvailability } from '../api/clientApi';
import './ClientForm.css';

const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Editing flow only: flag when a changed email belongs to another client.
const DUPLICATE_EMAIL_ERROR = 'An account with this email already exists in this brokerage';
// Add flow: when the entered email matches an existing client (different from the
// relationship's primary), prompt the advisor to import that client's details.
const EXISTING_EMAIL_ERROR = 'A client with this email already exists in your brokerage.';
// Add flow: the new (secondary) client can't reuse the primary client's email.
const PRIMARY_EMAIL_ERROR = 'Email must be different from the primary client of this relationship.';
// Shown once the existing client's details have been imported (form locked).
const EXISTING_CLIENT_NOTICE =
  'Existing client — using their saved details. This person will be added to this relationship; their information won’t change.';

function buildDefaultFormData(): ClientCreateDTO {
  const defaultCountry = SUPPORTED_COUNTRIES.find(c => c.value === DEFAULT_COUNTRY);
  return {
    email: '',
    firstName: '',
    lastName: '',
    phone: `+${getCountryCallingCode(DEFAULT_PHONE_COUNTRY)} `,
    crmPersonNumber: '',
    gender: '' as Gender,
    maritalStatus: '' as MaritalStatus,
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    townCity: '',
    stateProvinceCounty: '',
    postalCode: '',
    countryCode: DEFAULT_COUNTRY,
    country: defaultCountry?.text || 'Ireland',
    dateOfBirth: '',
    status: 'active',
  };
}

function derivePhoneCountry(phone?: string): string {
  if (!phone) return DEFAULT_PHONE_COUNTRY;
  for (const cc of phoneCountries) {
    if (phone.startsWith(`+${getCountryCallingCode(cc)}`)) return cc;
  }
  return DEFAULT_PHONE_COUNTRY;
}

// Map the existing-client lookup payload into the form fields (Add flow reuse).
function existingToFormData(a: EmailAvailability): ClientCreateDTO {
  const countryCode = a.countryCode || DEFAULT_COUNTRY;
  const country = a.country || SUPPORTED_COUNTRIES.find(c => c.value === countryCode)?.text || 'Ireland';
  return {
    email: a.email || '',
    firstName: a.firstName || '',
    lastName: a.lastName || '',
    phone: a.phone || `+${getCountryCallingCode(DEFAULT_PHONE_COUNTRY)} `,
    crmPersonNumber: a.crmPersonNumber || '',
    gender: (a.gender || '') as Gender,
    maritalStatus: (a.maritalStatus || '') as MaritalStatus,
    addressLine1: a.addressLine1 || '',
    addressLine2: a.addressLine2 || '',
    addressLine3: a.addressLine3 || '',
    townCity: a.townCity || '',
    stateProvinceCounty: a.stateProvinceCounty || '',
    postalCode: a.postalCode || '',
    countryCode,
    country,
    dateOfBirth: a.dateOfBirth || '',
    status: 'active',
  };
}

function EmptySquareIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
      <path d="M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H15C20.43 1.25 22.75 3.57 22.75 9V15C22.75 20.43 20.43 22.75 15 22.75ZM9 2.75C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V9C21.25 4.39 19.61 2.75 15 2.75H9Z" fill="#2D0A6C"/>
    </svg>
  );
}

function CheckedSquareIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
      <path d="M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H15C20.43 1.25 22.75 3.57 22.75 9V15C22.75 20.43 20.43 22.75 15 22.75ZM9 2.75C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V9C21.25 4.39 19.61 2.75 15 2.75H9Z" fill="#2D0A6C"/>
      <path d="M9 2.75C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V9C21.25 4.39 19.61 2.75 15 2.75H9Z" fill="#2D0A6C"/>
      <path d="M10.5799 15.5796C10.3799 15.5796 10.1899 15.4996 10.0499 15.3596L7.21994 12.5296C6.92994 12.2396 6.92994 11.7596 7.21994 11.4696C7.50994 11.1796 7.98994 11.1796 8.27994 11.4696L10.5799 13.7696L15.7199 8.62961C16.0099 8.33961 16.4899 8.33961 16.7799 8.62961C17.0699 8.91961 17.0699 9.39961 16.7799 9.68961L11.1099 15.3596C10.9699 15.4996 10.7799 15.5796 10.5799 15.5796Z" fill="white"/>
    </svg>
  );
}

function SameAsPrimaryCheckbox({ checked, onChange }: Readonly<{ checked: boolean; onChange: (v: boolean) => void }>) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      className="flex items-center gap-sm mt-sm bg-transparent border-none p-0 cursor-pointer focus:outline-none focus-visible:outline-none"
    >
      {checked ? <CheckedSquareIcon /> : <EmptySquareIcon />}
      <span className="text-[#000] font-sans text-body-02 font-normal leading-normal">
        Same as primary client
      </span>
    </button>
  );
}

type ClientFormProps = Readonly<{
  client?: Client | null;
  primaryClient?: Client | null;
  relationshipType?: RelationshipType;
  onSubmit: (data: ClientCreateDTO | ClientUpdateDTO, relationshipUpdate?: RelationshipUpdatePayload) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}>;

export function ClientForm({ client, primaryClient, relationshipType, onSubmit, onCancel, loading = false }: ClientFormProps) {
  const isEditing = !!client;

  const GENDER_OPTIONS = [
    { value: 'MALE', text: 'Male' },
    { value: 'FEMALE', text: 'Female' },
  ];

  const MARITAL_STATUS_OPTIONS = [
    { value: 'SINGLE', text: 'Single' },
    { value: 'DIVORCED', text: 'Divorced' },
    { value: 'WIDOWED', text: 'Widowed' },
    { value: 'MARRIED', text: 'Married' },
    { value: 'CIVIL_PARTNERSHIP', text: 'Civil Partnership' },
    { value: 'SEPARATED', text: 'Separated' },
  ];

  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY);
  const [formData, setFormData] = useState<ClientCreateDTO | ClientUpdateDTO>(buildDefaultFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const emailCheckTokenRef = useRef(0);
  // Add flow: whether the form is locked onto an existing client (auto-filled).
  const [locked, setLocked] = useState(false);
  const lockedRef = useRef(false);
  // Add flow: existing-client match for the typed email, awaiting "Import Client Data".
  const [existingMatch, setExistingMatch] = useState<EmailAvailability | null>(null);
  // Add-secondary flow: copy the primary client's country / address.
  const [sameAsCountry, setSameAsCountry] = useState(false);
  const [sameAsAddress, setSameAsAddress] = useState(false);
  // SOLO→JOINT conversion: 2nd modal step capturing the new relationship info.
  const [step, setStep] = useState<1 | 2>(1);
  const [relationshipName, setRelationshipName] = useState('');
  const [crmRelationshipNumber, setCrmRelationshipNumber] = useState('');

  const showSameAsPrimary = !isEditing && !!primaryClient && !locked;
  // Adding a client to a SOLO relationship promotes it to JOINT; gather the new
  // relationship name / CRM number in a 2nd step before submitting.
  const isSoloConversion = !isEditing && relationshipType === 'SOLO';

  const currentClientEmail = client?.email?.trim().toLowerCase() ?? '';

  const handleSameAsCountry = (v: boolean) => {
    setSameAsCountry(v);
    if (v && primaryClient) {
      setFormData(prev => ({ ...prev, countryCode: primaryClient.countryCode, country: primaryClient.country }));
      setErrors(({ country: _c, countryCode: _cc, ...rest }) => rest);
    }
  };

  const handleSameAsAddress = (v: boolean) => {
    setSameAsAddress(v);
    if (v && primaryClient) {
      setFormData(prev => ({
        ...prev,
        addressLine1: primaryClient.addressLine1,
        addressLine2: primaryClient.addressLine2 ?? '',
        addressLine3: primaryClient.addressLine3 ?? '',
        townCity: primaryClient.townCity,
        stateProvinceCounty: primaryClient.stateProvinceCounty ?? '',
        postalCode: primaryClient.postalCode,
      }));
      setErrors(({ addressLine1: _a, townCity: _t, postalCode: _p, ...rest }) => rest);
    }
  };

  const handleEmailBlur = async () => {
    const trimmed = formData.email.trim();
    if (!trimmed || !EMAIL_FORMAT_REGEX.test(trimmed)) {
      return;
    }
    if (trimmed.toLowerCase() === currentClientEmail) {
      if (errors.email === DUPLICATE_EMAIL_ERROR) {
        setErrors(prev => ({ ...prev, email: '' }));
      }
      return;
    }

    // Add flow: the typed email belongs to the relationship's primary client —
    // can't add the same person again. Warn and skip the existence lookup.
    if (!isEditing && primaryClient?.email && trimmed.toLowerCase() === primaryClient.email.trim().toLowerCase()) {
      setExistingMatch(null);
      setErrors(prev => ({ ...prev, email: PRIMARY_EMAIL_ERROR }));
      return;
    }

    const token = ++emailCheckTokenRef.current;
    try {
      const availability = await clientApi.checkEmailExists(trimmed);
      if (token !== emailCheckTokenRef.current) {
        return;
      }

      // Editing flow: keep the duplicate-email guard, never auto-fill/lock.
      if (isEditing) {
        if (availability.exists) {
          setErrors(prev => ({ ...prev, email: DUPLICATE_EMAIL_ERROR }));
        } else if (errors.email === DUPLICATE_EMAIL_ERROR) {
          setErrors(prev => ({ ...prev, email: '' }));
        }
        return;
      }

      // Add flow: surface an "Import Client Data" button instead of auto-filling.
      if (availability.exists) {
        setExistingMatch(availability);
        setErrors(prev => ({ ...prev, email: EXISTING_EMAIL_ERROR }));
      } else {
        setExistingMatch(null);
        if (errors.email === EXISTING_EMAIL_ERROR) {
          setErrors(prev => ({ ...prev, email: '' }));
        }
      }
    } catch (err) {
      console.warn('Email lookup failed; deferring to backend reuse/backstop', err);
    }
  };

  // Pull the matched existing client's details into the form and lock it
  // (email stays editable so a typo can be corrected).
  const handleImportClient = () => {
    if (!existingMatch) return;
    const importedEmail = existingMatch.email || formData.email.trim();
    setFormData({ ...existingToFormData(existingMatch), email: importedEmail });
    setPhoneCountryCode(derivePhoneCountry(existingMatch.phone));
    setErrors({});
    lockedRef.current = true;
    setLocked(true);
    setExistingMatch(null);
  };

  useEffect(() => {
    if (client) {
      setFormData({
        email: client.email,
        firstName: client.firstName,
        lastName: client.lastName,
        phone: client.phone,
        crmPersonNumber: client.crmPersonNumber || '',
        gender: client.gender || '' as Gender,
        maritalStatus: client.maritalStatus || '' as MaritalStatus,
        addressLine1: client.addressLine1,
        addressLine2: client.addressLine2 || '',
        addressLine3: client.addressLine3 || '',
        townCity: client.townCity,
        stateProvinceCounty: client.stateProvinceCounty || '',
        postalCode: client.postalCode,
        countryCode: client.countryCode,
        country: client.country,
        dateOfBirth: client.dateOfBirth,
        status: client.status,
      });
    }
    // Reset the wizard step whenever the modal is (re)opened for a client.
    setStep(1);
    setRelationshipName('');
    setCrmRelationshipNumber('');
    setExistingMatch(null);
  }, [client]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    } else if (
      !isEditing &&
      primaryClient?.email &&
      formData.email.trim().toLowerCase() === primaryClient.email.trim().toLowerCase()
    ) {
      newErrors.email = PRIMARY_EMAIL_ERROR;
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.length > 100) {
      newErrors.firstName = 'First name must not exceed 100 characters';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.length > 100) {
      newErrors.lastName = 'Last name must not exceed 100 characters';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format. Use international format (e.g., +14165550123)';
    } else if (formData.phone.length > 20) {
      newErrors.phone = 'Phone number must not exceed 20 characters';
    }

    if (formData.crmPersonNumber && formData.crmPersonNumber.length > 50) {
      newErrors.crmPersonNumber = 'CRM person number must not exceed 50 characters';
    }

    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address line 1 is required';
    } else if (formData.addressLine1.length > 255) {
      newErrors.addressLine1 = 'Address line 1 must not exceed 255 characters';
    }

    if (formData.addressLine2 && formData.addressLine2.length > 255) {
      newErrors.addressLine2 = 'Address line 2 must not exceed 255 characters';
    }

    if (formData.addressLine3 && formData.addressLine3.length > 255) {
      newErrors.addressLine3 = 'Address line 3 must not exceed 255 characters';
    }

    if (!formData.townCity.trim()) {
      newErrors.townCity = 'Town/City is required';
    } else if (formData.townCity.length > 100) {
      newErrors.townCity = 'Town/City must not exceed 100 characters';
    }

    if (formData.stateProvinceCounty && formData.stateProvinceCounty.length > 100) {
      newErrors.stateProvinceCounty = 'State/Province/County must not exceed 100 characters';
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Postal code is required';
    } else if (formData.postalCode.length > 20) {
      newErrors.postalCode = 'Postal code must not exceed 20 characters';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    } else if (formData.country.length > 100) {
      newErrors.country = 'Country must not exceed 100 characters';
    }

    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();

      if (birthDate > today) {
        newErrors.dateOfBirth = 'Date of birth must be in the past';
      } else {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 18) {
          newErrors.dateOfBirth = 'Client must be at least 18 years old';
        }
      }
    } else {
      newErrors.dateOfBirth = 'Date of birth is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async (relationshipUpdate?: RelationshipUpdatePayload) => {
    const clientData = {
      ...formData,
      status: 'active' as const,
      gender: formData.gender || undefined,
      maritalStatus: formData.maritalStatus || undefined,
    };
    try {
      if (relationshipUpdate) {
        await onSubmit(clientData, relationshipUpdate);
      } else {
        await onSubmit(clientData);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  // Step 1 primary action: validate the client, then either advance to the
  // relationship step (SOLO→JOINT) or submit directly.
  const handleClientContinue = () => {
    if (!validate()) {
      return;
    }
    if (errors.email === DUPLICATE_EMAIL_ERROR) {
      return;
    }
    if (isSoloConversion) {
      setStep(2);
      return;
    }
    void submit();
  };

  const handleFinish = () => {
    if (!relationshipName.trim()) {
      return;
    }
    void submit({
      relationshipName: relationshipName.trim(),
      crmRelationshipNumber: crmRelationshipNumber.trim() || undefined,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      handleClientContinue();
    } else {
      handleFinish();
    }
  };

  const handleCancel = () => {
    setStep(1);
    onCancel();
  };

  const handleChange = (field: keyof ClientCreateDTO, value: string) => {
    // Editing the email invalidates a pending import suggestion; if the form was
    // locked onto an imported client, unlock and reset to a fresh editable form
    // (preserving the typed email) so the advisor can enter a new client.
    if (field === 'email') {
      setExistingMatch(null);
      if (lockedRef.current) {
        lockedRef.current = false;
        setLocked(false);
        setFormData({ ...buildDefaultFormData(), email: value });
        setPhoneCountryCode(DEFAULT_PHONE_COUNTRY);
        setErrors({});
        return;
      }
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="client-form-modal">
      <div className="flex items-center justify-between p-md">
        <div className="flex items-center gap-sm">
          <AppIcon name={isEditing ? 'edit title' : 'add title'} size={24} color="#141414" />
          <h2 className="text-[#1B181E] !m-0 text-body-01 font-medium leading-normal font-sans">
            {isEditing ? 'Edit client' : 'Add Client'}
          </h2>
        </div>
        <IconButton
          variant="ghost"
          size="xs"
          icon={<AppIcon name="close" size={24} color="#141414" />}
          onClick={handleCancel}
          type="button"
          aria-label="Close"
        />
      </div>
      <div className="client-form-modal__divider" />

      <div className="client-form-modal__body">
        <form onSubmit={handleSubmit}>
          {step === 1 && (
          <div className="grid grid-cols-2 gap-x-lg gap-y-md">
          {Object.values(errors).some(Boolean) && (
            <div className="col-span-2">
              <Alert variant="error" header="Please correct the following errors:">
                <ul>
                  {Object.values(errors).filter(Boolean).map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </Alert>
            </div>
          )}

          {locked && (
            <div className="col-span-2">
              <Alert variant="info">{EXISTING_CLIENT_NOTICE}</Alert>
            </div>
          )}

          <div>
            <InputLabel required>Email address</InputLabel>
            <FormInput
              type="email"
              placeholder="Email address"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={handleEmailBlur}
              error={errors.email}
            />
            {existingMatch && !locked && (
              <button
                type="button"
                onClick={handleImportClient}
                className="mt-2 inline-flex items-center h-[36px] px-md rounded-lg bg-purple-primary hover:bg-[#220852] text-white text-body-03 font-medium font-sans transition-colors cursor-pointer border-none"
              >
                Import Client Data
              </button>
            )}
          </div>

          <div>
            <InputLabel required>First name</InputLabel>
            <FormInput
              placeholder="First name"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              error={errors.firstName}
              disabled={locked}
            />
          </div>

          <div>
            <InputLabel required>Last name</InputLabel>
            <FormInput
              placeholder="Last name"
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              error={errors.lastName}
              disabled={locked}
            />
          </div>

          <div>
            <InputLabel required>Phone number</InputLabel>
            <div className="flex gap-2">
              <PhoneFlagDropdown
                value={phoneCountryCode}
                onSelect={(alpha2, dialCode) => {
                  setPhoneCountryCode(alpha2);
                  handleChange('phone', `+${dialCode} `);
                }}
                disabled={locked}
              />
              <FormInput
                type="tel"
                placeholder="+353 85 234 5678"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="flex-1"
                disabled={locked}
              />
            </div>
            {errors.phone && <span className="block mt-sm font-sans text-body-03 font-normal text-[var(--input-error-text)]">{errors.phone}</span>}
          </div>

          <div className="flex flex-col gap-sm">
            <Dropdown
              label="Country of residential address"
              required
              options={SUPPORTED_COUNTRIES.map(c => ({ value: c.value, text: c.text }))}
              value={formData.countryCode}
              onChange={(value) => {
                const country = SUPPORTED_COUNTRIES.find(c => c.value === value);
                if (country) {
                  handleChange('countryCode', country.value);
                  handleChange('country', country.text);
                  handleChange('postalCode', '');
                }
              }}
              error={errors.country || errors.countryCode}
              disabled={locked || sameAsCountry}
            />
            {showSameAsPrimary && (
              <SameAsPrimaryCheckbox checked={sameAsCountry} onChange={handleSameAsCountry} />
            )}
          </div>

          <div className="flex flex-col gap-sm">
            <div>
              <InputLabel required>Address line 1</InputLabel>
              <FormInput
                placeholder="Street address, P.O. box"
                value={formData.addressLine1}
                onChange={(e) => handleChange('addressLine1', e.target.value)}
                error={errors.addressLine1}
                disabled={locked || sameAsAddress}
              />
            </div>
            {showSameAsPrimary && (
              <SameAsPrimaryCheckbox checked={sameAsAddress} onChange={handleSameAsAddress} />
            )}
          </div>

          <div>
            <InputLabel>Address line 2</InputLabel>
            <FormInput
              placeholder="Apartment, suite, unit, building, floor, etc."
              value={formData.addressLine2}
              onChange={(e) => handleChange('addressLine2', e.target.value)}
              error={errors.addressLine2}
              disabled={locked || sameAsAddress}
            />
          </div>

          <div>
            <InputLabel>Address line 3</InputLabel>
            <FormInput
              placeholder="Additional address information"
              value={formData.addressLine3}
              onChange={(e) => handleChange('addressLine3', e.target.value)}
              error={errors.addressLine3}
              disabled={locked || sameAsAddress}
            />
          </div>

          <div>
            <InputLabel required>Town/City</InputLabel>
            <FormInput
              placeholder={getCountryConfig(formData.countryCode).townCityPlaceholder}
              value={formData.townCity}
              onChange={(e) => handleChange('townCity', e.target.value)}
              error={errors.townCity}
              disabled={locked || sameAsAddress}
            />
          </div>

          <div>
            <InputLabel>{getCountryConfig(formData.countryCode).stateProvinceLabel}</InputLabel>
            <FormInput
              placeholder={getCountryConfig(formData.countryCode).stateProvincePlaceholder}
              value={formData.stateProvinceCounty}
              onChange={(e) => handleChange('stateProvinceCounty', e.target.value)}
              error={errors.stateProvinceCounty}
              disabled={locked || sameAsAddress}
            />
          </div>

          <div>
            <InputLabel required>{getCountryConfig(formData.countryCode).postalCodeLabel}</InputLabel>
            <FormInput
              placeholder={getCountryConfig(formData.countryCode).postalCodePlaceholder}
              value={formData.postalCode}
              onChange={(e) => handleChange('postalCode', normalizePostalCode(formData.countryCode, e.target.value))}
              error={errors.postalCode}
              disabled={locked || sameAsAddress}
            />
          </div>

          <DateTimeInput
            label="Date of birth"
            value={formData.dateOfBirth}
            onChange={(val) => handleChange('dateOfBirth', val)}
            error={errors.dateOfBirth}
            required
            disabled={locked}
          />

          <Dropdown
            label="Gender"
            options={GENDER_OPTIONS}
            value={formData.gender}
            onChange={(value) => handleChange('gender', value)}
            error={errors.gender}
            placeholder="Select gender"
            disabled={locked}
          />

          <Dropdown
            label="Marital status"
            options={MARITAL_STATUS_OPTIONS}
            value={formData.maritalStatus}
            onChange={(value) => handleChange('maritalStatus', value)}
            error={errors.maritalStatus}
            placeholder="Select marital status"
            disabled={locked}
          />

          <div>
            <InputLabel required>CRM person number</InputLabel>
            <FormInput
              placeholder="CRM person number"
              value={formData.crmPersonNumber}
              onChange={(e) => handleChange('crmPersonNumber', e.target.value)}
              error={errors.crmPersonNumber}
              disabled={locked}
            />
          </div>

          </div>
          )}

          {step === 2 && (
          <div>
            <p className="m-0 mb-xl text-[#000] font-sans text-body-01 font-normal leading-normal">
              By adding a new client to a solo relationship, <span className="font-bold">it changes to a joint relationship</span>. Please update the relationship name and CRM number to reflect this.
            </p>
            <div className="grid grid-cols-3 gap-lg items-end">
              <Dropdown
                label="Relationship type"
                required
                options={[{ value: 'JOINT', text: 'Joint' }]}
                value="JOINT"
                disabled
              />
              <div>
                <InputLabel required>Relationship name</InputLabel>
                <FormInput
                  placeholder="Relationship name"
                  value={relationshipName}
                  onChange={(e) => setRelationshipName(e.target.value)}
                />
              </div>
              <div>
                <InputLabel>CRM relationship number</InputLabel>
                <FormInput
                  placeholder="CRM relationship number"
                  value={crmRelationshipNumber}
                  onChange={(e) => setCrmRelationshipNumber(e.target.value)}
                />
              </div>
            </div>
          </div>
          )}
        </form>
      </div>

      <div className="client-form-modal__divider" />
      <div className="client-form-modal__footer">
        <Button
          type="button"
          variant="outlined"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        {step === 1 ? (
          <Button
            type="button"
            variant="filled"
            onClick={handleClientContinue}
            disabled={loading}
            loading={loading}
          >
            {isEditing ? 'Update Client' : (isSoloConversion ? 'Next' : 'Add Client')}
          </Button>
        ) : (
          <Button
            type="button"
            variant="filled"
            onClick={handleFinish}
            disabled={loading || !relationshipName.trim()}
            loading={loading}
          >
            Finish
          </Button>
        )}
      </div>
    </div>
  );
}
